import * as React from 'react';
import { cx } from 'linaria';
import { useTheme, ThemeProvider } from '../index';
import * as styles from './button-group.styles';
import * as ToggleGroup from '@radix-ui/react-toggle-group';

export interface ButtonGroupItemProps {
    value: string;
    content: React.ReactNode | string;
    disabled: boolean;
}
export interface ButtonGroupProps extends Omit<ToggleGroup.ToggleGroupSingleProps, 'type'> {
    /**
     * contains the details of the button
     */
    items: ButtonGroupItemProps[];

    /**
     * Size of the button.
     * @default medium
     */
    size?: 'small' | 'medium';

    /**
     * Occupies full width.
     * @default false
     */
    stretched?: boolean;

    /**
     * Test ID for the component.
     */
    testId?: string;

    /**
     * Theme override.
     */
    themeOverride?: 'light' | 'dark';
}

export const ButtonGroup: React.FC<ButtonGroupProps> = ({
    items,
    size = 'medium',
    stretched = false,
    themeOverride,
    value,
    onValueChange,
    ...rest
}) => {
    const currentTheme = useTheme();
    const theme = themeOverride ? themeOverride : currentTheme;

    const className = cx(styles.toggleGroupItem, !stretched && styles.sizes[size], stretched && styles.stretched[size]);

    const buttons = items.map((button) => (
        <ToggleGroup.Item key={button.value} className={cx(className)} value={button.value} disabled={button.disabled}>
            {button.content}
        </ToggleGroup.Item>
    ));

    return (
        <ThemeProvider theme={theme}>
            <ToggleGroup.Root
                className={cx(
                    !stretched && styles.toggleGroupContainer,
                    stretched && styles.stretchedToggleGroupContainer,
                )}
                value={value}
                type="single"
                onValueChange={onValueChange}
                {...rest}
            >
                {buttons}
            </ToggleGroup.Root>
        </ThemeProvider>
    );
};
